import { BigNumber } from 'bignumber.js';
import { Tokens } from '~/utils/constants';
import type { TwoTokenLicense } from '~/types/licenses';
import { formatEther } from 'ethers';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';

export default () => {
  const store = useMainStore();
  const { currentToken } = storeToRefs(store);
  const { address } = useWeb3ModalAccount();
  const { blockchain } = useEnvs();
  const { availableTokens } = useTokensReader();
  const { minedBalance } = useMinedBalance();

  const minedBalanceClaimed = computed(() => minedBalance.value?.[2]);
  const currentSecondaryTokenBalance = computed(() => minedBalance.value?.[3]);
  const currentPrimaryTokenBalance = computed(() => minedBalance.value?.[4]);

  const availableForProducing = ref<TwoTokenLicense>({ primaryToken: '0', secondaryToken: '0' });
  const isLoading = ref(false);

  let reqId = 0;
  let aborted = false;

  const getAvailableForProducing = async () => {
    const myId = ++reqId;
    isLoading.value = true;

    const tokenSnap = currentToken.value;
    const addrSnap = address.value;
    const isMine = blockchain.contracts[tokenSnap]?.addresses?.mine;

    try {
      if (!minedBalance.value || !addrSnap || !isMine) {
        if (myId === reqId) availableForProducing.value = { primaryToken: '0', secondaryToken: '0' };
        return;
      }

      // read licenses sum
      const boughtLicensesSum = await availableTokens(addrSnap, isMine);

      if (BigNumber(boughtLicensesSum).isZero()) {
        if (!aborted && myId === reqId) {
          availableForProducing.value = { primaryToken: '0', secondaryToken: '0' };
        }
        return;
      }

      const primaryTokenValueFormatted = BigNumber(formatEther(currentPrimaryTokenBalance.value || 0))
        .minus(formatEther(minedBalanceClaimed.value || 0))
        .decimalPlaces(2)
        .toNumber();

      const next: TwoTokenLicense = {
        primaryToken: BigNumber(primaryTokenValueFormatted).isNegative() ? '0' : primaryTokenValueFormatted.toString(),
        secondaryToken: BigNumber(formatEther(currentSecondaryTokenBalance.value || 0)).toString()
      };

      if (!aborted && myId === reqId && tokenSnap === currentToken.value && addrSnap === address.value) {
        availableForProducing.value = next;
      }
    } catch (e) {
      if (!aborted && myId === reqId) console.log(e);
    } finally {
      if (myId === reqId) isLoading.value = false;
    }
  };

  const primaryToken = computed(() => {
    const v = availableForProducing.value?.primaryToken || 0;
    return BigNumber(v).toNumber();
  });

  const secondaryToken = computed(() => {
    if (store.currentToken === Tokens.sand) return 0;
    return BigNumber(availableForProducing.value?.secondaryToken || 0).toNumber();
  });

  // react to the real drivers only (token/address/minedBalance)
  watch(
    [() => currentToken.value, () => address.value, () => minedBalance.value],
    () => {
      // clear promptly to avoid showing values from the previous tab
      availableForProducing.value = { primaryToken: '0', secondaryToken: '0' };
      getAvailableForProducing();
    },
    { immediate: true, deep: true }
  );

  onUnmounted(() => {
    aborted = true;
    reqId++;
  });

  return {
    primaryToken,
    secondaryToken,
    isLoading,
    getAvailableForProducing
  };
};
